import { PDFCheckBox, PDFDict, PDFDocument, PDFName, PDFRadioGroup, PDFRef } from 'pdf-lib';

// Composites the text-only jsPDF layer over the form's ORIGINAL PDF pages.
//
// The report used to embed a 200-DPI PNG of each page as its background, so the
// finished document was a photograph of a form with some text on top: blurry
// when zoomed or printed, and enormous. The backend now retains the uploaded
// PDF (InspectionReport.source_file), so the real page content can be used
// instead — vector line work, sharp at any magnification.
//
// pdf-lib is already a dependency and already used for exactly this kind of
// work: see Components/InvoiceTool/InvoiceUtil.ts (mergeJsPDFandBase64) and
// organisms/DownloadAllInspectionsForms/utils.ts (mergePDFs).

// jsPDF's default document is portrait/mm/A4, which is what every caller
// constructs, so the overlay pages are exactly this size.
const A4_W_PT = 595.28;
const A4_H_PT = 841.89;

/**
 * Strip the host off a media URL so the fetch stays same-origin.
 *
 * DRF renders FileField as an ABSOLUTE url against the API host, which is a
 * different origin from the app in dev (:5050 vs :8080). An <img src> does not
 * care, which is why the existing previews work, but fetch() is subject to
 * CORS and would fail. Both the dev proxy and nginx serve /media/ from this
 * origin, so the path alone resolves correctly.
 */
const toSameOrigin = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
};

/**
 * Fetch the form's source PDF, or null when there isn't one.
 *
 * Null is the normal case for any form uploaded before source_file existed —
 * callers fall back to the old raster background rather than failing.
 */
export const fetchSourcePdf = async (url?: string | null): Promise<ArrayBuffer | null> => {
  if (!url) return null;
  try {
    const response = await fetch(toSameOrigin(url), { credentials: 'same-origin' });
    if (!response.ok) return null;
    const bytes = await response.arrayBuffer();
    return bytes.byteLength > 0 ? bytes : null;
  } catch {
    // A missing media file or a CORS problem must not break report generation.
    return null;
  }
};

/**
 * Give every widget pdf-lib cannot flatten an EMPTY appearance stream.
 *
 * `flatten()` defaults to regenerating appearances first, and that is what put a
 * SECOND square on top of every checkbox: these blank forms print their boxes as
 * text glyphs and then park a checkbox widget over them, and a widget whose /AP
 * carries only an "on" state (no /Off) counts as needing an update. pdf-lib then
 * synthesises one from `defaultCheckBoxAppearanceProvider`, which strokes a
 * rectangle over the widget's rect — a box the form never had. pdf2image doesn't
 * invent it, which is why the fill canvas showed one square and the report two.
 *
 * So appearances must NOT be regenerated. The catch is that flatten then throws
 * on the very widgets that have nothing to draw, taking the whole page's widget
 * artwork with it (see the flatten comment below). Handing those widgets a blank
 * XObject keeps flatten on the happy path and draws exactly what a viewer would:
 * nothing.
 *
 * Widgets that DO have a real appearance for their current state are untouched,
 * so the forms whose squares are genuinely widget artwork still get them.
 */
const blankUnflattenableWidgets = (doc: PDFDocument) => {
  const { context } = doc;
  let blankRef: PDFRef | null = null;
  const blankAppearance = () => {
    if (!blankRef) blankRef = context.register(context.formXObject([]));
    return blankRef;
  };

  doc.getForm().getFields().forEach(field => {
    field.acroField.getWidgets().forEach(widget => {
      try {
        // Read /AP -> /N raw: pdf-lib's flatten wants a REF, and the widget
        // helpers that dereference it would hide the difference.
        const normal = widget.AP()?.get(PDFName.of('N'));
        if (normal instanceof PDFRef) return;

        // A check box / radio keeps its states in a dict; flatten looks up the
        // field's current value, then falls back to /Off.
        if (
          normal instanceof PDFDict &&
          (field instanceof PDFCheckBox || field instanceof PDFRadioGroup)
        ) {
          const state =
            normal.get(field.acroField.getValue()) ?? normal.get(PDFName.of('Off'));
          if (state instanceof PDFRef) return;
        }

        widget.setNormalAppearance(blankAppearance());
      } catch {
        // Never let one malformed widget cost the whole background.
      }
    });
  });
};

/**
 * Draw `overlayBytes` (a transparent, text-only PDF) over `sourceBytes`.
 *
 * Returns the merged bytes, or null if either document can't be parsed — the
 * caller then keeps whatever it already had.
 */
export const mergeOntoSourcePdf = async (
  overlayBytes: ArrayBuffer,
  sourceBytes: ArrayBuffer,
): Promise<Uint8Array | null> => {
  try {
    const overlayDoc = await PDFDocument.load(overlayBytes);
    const sourceDoc = await PDFDocument.load(sourceBytes);

    // Bake the AcroForm layer into the page content before embedding.
    //
    // These blank forms are Word/Acrobat documents, and a lot of what LOOKS
    // like printed artwork is actually widget annotations — the St Pete form
    // carries 67 checkbox widgets that draw its squares. `embedPage` builds an
    // XObject from the content stream ONLY, so every one of those disappeared
    // and the report came out with the labels but no boxes. pdf2image renders
    // widget appearances, which is why the raster background never showed the
    // problem and the builder canvas looks correct.
    //
    // Flattening draws each widget's appearance stream into the page, so the
    // artwork survives and nothing stays interactive — the same end state the
    // server-side regular-form renderer reaches by stripping /Annots.
    //
    // `updateFieldAppearances: false` is load-bearing: the default REGENERATES
    // any appearance pdf-lib considers stale, which stamped an extra square onto
    // every checkbox of every form whose widgets have no /Off state (see
    // blankUnflattenableWidgets). Draw what the form ships with, nothing else.
    try {
      blankUnflattenableWidgets(sourceDoc);
      sourceDoc.getForm().flatten({ updateFieldAppearances: false });
    } catch {
      // A malformed or appearance-less AcroForm must not cost us the whole
      // vector background; embed the page as-is and lose only the widgets.
    }

    // Bail out on a rotated page rather than misplacing every field.
    //
    // Scans routinely carry /Rotate 90. pdf2image APPLIES that when it makes
    // the page images the placeholders were positioned against, but pdf-lib's
    // embedPage does not — so the artwork would land at 90 degrees to the
    // values. Returning null drops the caller back to the raster background,
    // which pdf2image already rotated correctly. Worse-looking, but right.
    if (sourceDoc.getPages().some(page => page.getRotation().angle % 360 !== 0)) {
      return null;
    }

    const out = await PDFDocument.create();

    const overlayPages = await out.embedPages(overlayDoc.getPages());
    const sourcePages = await out.embedPages(sourceDoc.getPages());

    for (let i = 0; i < overlayPages.length; i += 1) {
      const page = out.addPage([A4_W_PT, A4_H_PT]);

      // The source page is STRETCHED to fill A4, aspect ratio discarded.
      //
      // That looks wrong but is load-bearing: overlayInfo has always stretched
      // the page image to fill A4 (see PreviewTool/builder/coords.ts), so every
      // saved formMarkup coordinate encodes that exact distortion. Embedding a
      // Letter page at its true size would shift every field on every existing
      // form.
      const source = sourcePages[i];
      if (source) {
        page.drawPage(source, { x: 0, y: 0, width: A4_W_PT, height: A4_H_PT });
      }
      page.drawPage(overlayPages[i], { x: 0, y: 0, width: A4_W_PT, height: A4_H_PT });
    }

    return await out.save();
  } catch {
    return null;
  }
};

/**
 * Build the finished report: text-only jsPDF doc + source PDF -> Blob.
 *
 * Falls back to the doc as-is (which callers render with the raster background)
 * whenever there is no usable source PDF.
 */
export const buildReportBlob = async (doc: any, sourceBytes: ArrayBuffer | null): Promise<Blob> => {
  if (!sourceBytes) {
    return doc.output('blob');
  }
  const merged = await mergeOntoSourcePdf(doc.output('arraybuffer'), sourceBytes);
  if (!merged) {
    return doc.output('blob');
  }
  return new Blob([merged], { type: 'application/pdf' });
};
